import { z } from 'zod';
import { supabase } from './supabase';

/**
 * Creates a typed RPC contract for admin operations
 */
export function createRpcContract<TInput extends z.ZodType, TOutput extends z.ZodType>(
  functionName: string,
  inputSchema: TInput,
  outputSchema: TOutput
) {
  return {
    functionName,
    inputSchema,
    outputSchema,
  };
}

/**
 * Creates a typed RPC caller from a contract
 */
export function createRpcCaller<TInput extends z.ZodType, TOutput extends z.ZodType>(
  contract: {
    functionName: string;
    inputSchema: TInput;
    outputSchema: TOutput;
  }
) {
  return async (input: z.infer<TInput>): Promise<{
    data: z.infer<TOutput> | null;
    error: Error | null;
  }> => {
    // Validate input
    const parseResult = contract.inputSchema.safeParse(input);
    if (!parseResult.success) {
      return {
        data: null,
        error: new Error(`Invalid input: ${parseResult.error.message}`),
      };
    }

    // Call Supabase RPC
    const { data, error } = await supabase.rpc(contract.functionName, parseResult.data);

    if (error) {
      return {
        data: null,
        error: new Error(error.message),
      };
    }

    // Validate output
    const outputResult = contract.outputSchema.safeParse(data);
    if (!outputResult.success) {
      console.warn(`RPC ${contract.functionName} output validation failed:`, outputResult.error);
      // Return data anyway, validation is informational
      return {
        data: data as z.infer<TOutput>,
        error: null,
      };
    }

    return {
      data: outputResult.data,
      error: null,
    };
  };
}
